<template>
  <v-card>
    <v-card-title class="d-flex align-center justify-space-between">
      <span>Validar identidad</span>
      <v-btn icon variant="text" size="small" @click="$emit('cancel')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-alert type="info" variant="tonal" density="compact" class="mb-4">
        Simulación local: no llama a ningún backend. Elegí qué resultado querés
        que devuelva el <code>onValidate</code> de AlisonGUI.
      </v-alert>

      <div class="d-flex align-center ga-2 mb-4">
        <span class="text-caption text-medium-emphasis">Resultado a simular:</span>
        <v-btn-toggle v-model="simulateResult" mandatory density="comfortable" color="primary">
          <v-btn value="success" size="small">Éxito</v-btn>
          <v-btn value="error" size="small">Error</v-btn>
        </v-btn-toggle>
      </div>

      <v-text-field
        v-if="simulateResult === 'error'"
        v-model="errorMessage"
        label="Mensaje de error a simular"
        placeholder="Error simulado de validación de identidad"
        density="compact"
        class="mb-2"
      />

      <v-alert v-if="lastError" type="error" variant="tonal" density="compact" class="mb-4">
        {{ lastError }}
      </v-alert>

      <div class="d-flex flex-column">
        <v-tabs v-model="activeTab" align-tabs="center">
          <v-tab value="0" class="justify-center py-3">Renaper (ARG)</v-tab>
          <v-tab value="1" class="justify-center py-3">Manual (DNI + selfie)</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <!-- ARG / Renaper -->
          <v-window-item value="0" class="mx-1">
            <v-alert type="warning" variant="tonal" density="compact" class="my-3">
              El match debe ser exacto: nombre, tipo y número de documento, sexo y orden.
            </v-alert>
            <v-form ref="renaperForm" v-model="renaperValid">
              <v-text-field
                v-model="renaper.fullName"
                label="Nombre completo"
                :rules="[rules.notEmpty]"
                class="px-1"
              />
              <v-select
                v-model="renaper.documentType"
                :items="['CUIL', 'DNI']"
                label="Tipo de documento"
                :rules="[rules.notEmpty]"
                class="px-1"
              />
              <v-text-field
                v-model="renaper.documentValue"
                label="Número de documento"
                :rules="[rules.notEmpty]"
                class="px-1"
              />
              <v-select
                v-model="renaper.gender"
                :items="[{ title: 'Masculino', value: 'M' }, { title: 'Femenino', value: 'F' }, { title: 'Otro', value: 'X' }]"
                label="Sexo"
                :rules="[rules.notEmpty]"
                class="px-1"
              />
              <v-text-field
                v-model="renaper.order"
                label="Orden / clave de trámite"
                type="password"
                autocomplete="new-password"
                :rules="[rules.notEmpty]"
                class="px-1"
              />
            </v-form>
          </v-window-item>

          <!-- Manual -->
          <v-window-item value="1" class="mx-1">
            <v-row class="px-2 py-3" wrap>
              <v-col cols="12" md="4">
                <v-file-input
                  v-model="manual.dniFrontFile"
                  label="DNI frente"
                  accept="image/*"
                  prepend-icon="mdi-card-account-details-outline"
                  density="compact"
                  @update:model-value="f => onImageSelected('dniFront', f)"
                />
                <v-img v-if="manual.dniFront" :src="manual.dniFront" max-height="120" contain class="border rounded" />
              </v-col>
              <v-col cols="12" md="4">
                <v-file-input
                  v-model="manual.dniBackFile"
                  label="DNI dorso"
                  accept="image/*"
                  prepend-icon="mdi-card-account-details-outline"
                  density="compact"
                  @update:model-value="f => onImageSelected('dniBack', f)"
                />
                <v-img v-if="manual.dniBack" :src="manual.dniBack" max-height="120" contain class="border rounded" />
              </v-col>
              <v-col cols="12" md="4">
                <v-file-input
                  v-model="manual.selfieFile"
                  label="Selfie"
                  accept="image/*"
                  prepend-icon="mdi-face-recognition"
                  density="compact"
                  @update:model-value="f => onImageSelected('selfie', f)"
                />
                <v-img v-if="manual.selfie" :src="manual.selfie" max-height="120" contain class="border rounded" />
              </v-col>
            </v-row>
          </v-window-item>
        </v-window>
      </div>

      <v-divider class="my-5 mx-0" />

      <v-card-actions class="justify-end">
        <v-btn color="grey" variant="outlined" rounded="pill" class="mx-2" :disabled="loading" @click="$emit('cancel')">
          Cancelar
        </v-btn>
        <v-btn
          :disabled="!isValid"
          :loading="loading"
          color="primary"
          rounded="pill"
          variant="flat"
          @click="validateIdentity"
        >
          Verificar identidad
        </v-btn>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'VerifyIdentityDialog',
  data() {
    return {
      activeTab: '0',
      loading: false,
      simulateResult: 'success',
      errorMessage: '',
      lastError: '',
      renaperValid: false,
      renaper: {
        fullName: '',
        documentType: 'CUIL',
        documentValue: '',
        gender: '',
        order: '',
      },
      manual: {
        dniFrontFile: null,
        dniBackFile: null,
        selfieFile: null,
        dniFront: '',
        dniBack: '',
        selfie: '',
      },
      rules: {
        notEmpty: v => !!v || 'Requerido',
      },
    };
  },
  computed: {
    isValid() {
      if (this.activeTab === '0') return this.renaperValid;
      return !!(this.manual.dniFront || this.manual.dniBack || this.manual.selfie);
    },
  },
  methods: {
    onImageSelected(field, file) {
      const singleFile = Array.isArray(file) ? file[0] : file;
      if (!singleFile) {
        this.manual[field] = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.manual[field] = reader.result;
      };
      reader.readAsDataURL(singleFile);
    },
    async validateIdentity() {
      this.loading = true;
      this.lastError = '';

      await new Promise(resolve => setTimeout(resolve, 600));
      this.loading = false;

      if (this.simulateResult === 'error') {
        const err = new Error(this.errorMessage || 'Error simulado de validación de identidad');
        err.code = 'IDENTITY_VALIDATION_FAILED';
        this.lastError = err.message;
        this.$emit('error', err);
        return;
      }

      const mode = this.activeTab === '0' ? 'renaper' : 'alternative';
      this.$emit('success', { mode });
    },
  },
};
</script>
